import { action, observable, runInAction, makeObservable } from "mobx";
import { randmarApiWithTags } from "randmar-api-client";
import { store } from "../../new-store/store";
import SearchResult from "./SearchResult";

class SearchStore {
  currentQuery?: string = undefined;

  private nextQuery: string = "";
  searchResults: SearchResult[] = [];
  loading: boolean = false;
  searchResult?: SearchResult = undefined;

  constructor() {
    makeObservable(this, {
      currentQuery: observable,
      searchResults: observable,
      loading: observable,
      searchResult: observable,
      updateSearch: action.bound,
      getProduct: action.bound,
      validateProduct: action.bound,
      search: action.bound
    });
  }

  async updateSearch(applicationId: string, query: string) {
    if (this.loading) this.nextQuery = query;
    else this.search(applicationId, query);
  }

  async getProduct(applicationId: string, productNumber: string) {
    try {
      const product = await store.dispatch(
        randmarApiWithTags.endpoints.getV4ManufacturerByRouteManufacturerIdProductAndProductNumber.initiate({
          routeManufacturerId: applicationId,
          productNumber,
          withSpecification: true,
        })
      ).unwrap();
      const mappedSearchResult = SearchResult.convertFrom({ ContentType: "Product", Content: product }, undefined);

      runInAction(() => {
        this.searchResult = mappedSearchResult;
      });
    } catch (e) {
      console.error("Failed to get product:", e);
      runInAction(() => {
        this.searchResult = undefined;
      });
    }
  }

  async validateProduct(applicationId: string, randmarSKU: string): Promise<boolean> {
    try {
      await store.dispatch(
        randmarApiWithTags.endpoints.getV4ManufacturerByRouteManufacturerIdProductAndProductNumber.initiate({
          routeManufacturerId: applicationId,
          productNumber: randmarSKU,
          withSpecification: false,
        })
      ).unwrap();
      return true;
    } catch (e) {
      console.error("Failed to validate product:", e);
      return false;
    }
  }

  async search(applicationId: string, query: string) {
    if (this.currentQuery === query) return;
    if (query === "") return;

    this.currentQuery = query;
    this.loading = true;

    try {
      const response = await store.dispatch(
        randmarApiWithTags.endpoints.getV4ManufacturerByRouteManufacturerIdSearch.initiate({
          routeManufacturerId: applicationId,
          s: query,
        })
      ).unwrap();
      const results = Array.isArray(response) ? response : response ? [response] : [];
      const mappedSearchResults = results.map((r) => SearchResult.convertFrom(r, undefined));

      runInAction(() => {
        this.searchResults = mappedSearchResults;
        this.loading = false;

        if (this.nextQuery !== "") {
          this.search(applicationId, this.nextQuery);
          this.nextQuery = "";
        }
      });
    } catch (e) {
      console.error("Search failed:", e);
      runInAction(() => {
        this.searchResults = [];
        this.nextQuery = "";
        this.loading = false;
      });
    }
  }
}

export default SearchStore;
